import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { useSport } from "../context/SportContext";
import { TeamLogo } from "./TeamLogo";

const SPORT_COLOR = {
  NBA: "#F59E0B",
  WNBA: "#F97316",
  NFL: "#22C55E",
  MLB: "#38BDF8",
};

const ScoreCard = ({ g, onOpen }) => {
  const accent = SPORT_COLOR[g.sport] || "#00E5FF";
  const homeLead = Number(g.home.score) >= Number(g.away.score);
  const clickable = !!g.matchup_id;
  const row = (t, leading) => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <TeamLogo sport={g.sport} abbr={t.abbr} logo={t.logo} size="sm" />
        <span className={`font-data text-sm truncate ${leading ? "text-white" : "text-slate-400"}`}>
          {t.name || t.abbr}
        </span>
      </div>
      <span
        data-testid={`live-score-${g.id}-${t.abbr}`}
        className={`font-heading font-extrabold text-base tabular-nums ${leading ? "text-white" : "text-slate-500"}`}
      >
        {t.score}
      </span>
    </div>
  );

  return (
    <button
      type="button"
      data-testid={`live-score-card-${g.id}`}
      onClick={() => clickable && onOpen(g)}
      disabled={!clickable}
      title={clickable ? "Open props & AI picks" : "Sync this sport's live slate to open props"}
      className={`group text-left shrink-0 w-[200px] rounded-xl border border-[#233248] bg-[#141C2B] px-3 py-2.5 snap-start transition-colors ${
        clickable ? "cursor-pointer hover:border-[#00E5FF]/60 hover:bg-[#1B273E]" : "cursor-default"
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="font-data text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded"
          style={{ color: accent, background: `${accent}1a` }}
        >
          {g.sport}
        </span>
        <span className="flex items-center gap-1 text-[10px] font-data text-red-400 uppercase tracking-wide">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
          {g.clock && g.period ? `${g.clock} · ${g.short_detail}` : g.short_detail || "Live"}
        </span>
      </div>
      <div className="space-y-1">
        {row(g.away, !homeLead)}
        {row(g.home, homeLead)}
      </div>
      {clickable && (
        <div className="mt-1.5 flex items-center justify-end gap-0.5 text-[10px] font-data text-[#00E5FF] opacity-0 group-hover:opacity-100 transition-opacity">
          Props & AI <ChevronRight className="h-3 w-3" />
        </div>
      )}
    </button>
  );
};

export const LiveScores = () => {
  const [games, setGames] = useState([]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("liveScoresCollapsed") === "1");
  const timer = useRef(null);
  const nav = useNavigate();
  const { sport } = useSport();

  const load = () => {
    api
      .get("/live-scores")
      .then((r) => setGames(r.data.games || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("liveScoresCollapsed", c ? "0" : "1");
      return !c;
    });
  };

  const openGame = (g) => {
    nav(`/?sport=${g.sport}&source=live&open=${g.matchup_id}`);
  };

  const sportGames = games.filter((g) => g.sport === sport);
  if (!sportGames.length) return null;

  return (
    <div data-testid="live-scores-bar" className="sticky top-16 z-30 glass border-b border-[#233248]">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 flex items-center gap-3">
        <button
          data-testid="live-scores-collapse-toggle"
          onClick={toggleCollapsed}
          title={collapsed ? "Show live scores" : "Hide live scores"}
          className="flex items-center gap-1.5 shrink-0 text-[#00E5FF] font-data text-xs font-bold tracking-widest uppercase hover:text-white transition-colors"
        >
          <Radio className="h-4 w-4 animate-pulse" />
          <span className="hidden sm:inline">Live Now</span>
          <span className="rounded bg-[#00E5FF]/15 px-1.5 py-0.5 text-[10px]">{sportGames.length}</span>
          {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>
        {!collapsed && (
          <div className="flex gap-2.5 overflow-x-auto snap-x no-scrollbar">
            {sportGames.map((g) => (
              <ScoreCard key={`${g.sport}-${g.id}`} g={g} onOpen={openGame} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
